import { hasPermission } from 'core/auth/rbac'
import { CatalogueIcon } from 'core/components/Icons'
import { Badge } from 'core/components/ui/Badge'
import { Column, Table } from 'core/components/ui/Table'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import React from 'react'
import { CatalogItem } from '../../domain/entities/Catalogue'

interface SkuTableProps {
  items: CatalogItem[]
  loading: boolean
  onEdit: (item: CatalogItem) => void
  onDelete?: (item: CatalogItem) => void
  onRestore?: (item: CatalogItem) => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage: number
}

const formatCurrency = (val: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(val)
}

export const SkuTable: React.FC<SkuTableProps> = ({
  items,
  loading,
  onEdit,
  onDelete,
  onRestore,
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}) => {
  const { user } = useAuth()

  const columns: Column<CatalogItem>[] = [
    {
      key: 'id',
      label: 'SKU ID',
      className: 'font-mono text-xs text-text-secondary',
    },
    {
      key: 'name',
      label: 'Product Name',
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.images && row.images.length > 0 ? (
            <img
              src={row.images[0]}
              alt={row.name}
              className="w-10 h-10 rounded-btn object-cover border border-border"
            />
          ) : (
            <div className="w-10 h-10 rounded-btn bg-bg flex items-center justify-center border border-border text-text-secondary text-xs">
              No Img
            </div>
          )}
          <div className="flex flex-col">
            <span className="font-semibold text-text-primary">{row.name}</span>
            <span className="text-xs text-text-secondary line-clamp-1 max-w-[200px]">{row.description || '—'}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'variant',
      label: 'Unit / Packaging Size',
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-text-primary text-sm font-semibold">{row.variant}</span>
          <span className="text-text-secondary text-[10px]">
            {row.unit}
            {row.packWeightKg && row.unit !== 'Kg' ? ` · ${row.packWeightKg}kg` : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'moq',
      label: 'MOQ',
      render: (row) => (
        <span className="text-text-primary text-sm font-semibold">
          {row.moq} {row.unit || 'Units'}
        </span>
      ),
    },
    {
      key: 'sellingPrice',
      label: 'Selling Price',
      render: (row) => (
        <span className="text-primary text-sm font-bold">
          {formatCurrency(row.sellingPrice)} /{row.unit || 'Unit'}
        </span>
      ),
    },
    {
      key: 'manufacturerPrice',
      label: 'Manufacturer Price',
      render: (row) => (
        <span className="text-text-secondary text-sm font-semibold">
          {formatCurrency(row.manufacturerPrice)} /{row.unit || 'Unit'}
        </span>
      ),
    },
    {
      key: 'mrp',
      label: 'MRP',
      render: (row) => (
        <span className="text-text-secondary text-sm">
          {formatCurrency(row.mrp)} /{row.unit || 'Unit'}
        </span>
      ),
    },
    {
      key: 'isDeleted',
      label: 'Status',
      render: (row) => (
        <Badge tone={row.isDeleted ? 'danger' : 'success'} variant="subtle" size="sm" dot>
          {row.isDeleted ? 'Disabled' : 'Active'}
        </Badge>
      ),
    },

    {
      key: 'actions',
      label: 'Actions',
      className: 'text-center',
      render: (row) => {
        const canEdit = hasPermission(user, 'edit_catalogue')
        if (!canEdit) return null

        return (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => onEdit(row)}
              className="p-1.5 rounded-btn hover:bg-bg text-text-secondary hover:text-text-primary transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,255,255,0.14)] dark:hover:border-[rgba(255,255,255,0.28)]"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
              Edit
            </button>
            {row.isDeleted ? (
              <button
                onClick={() => onRestore?.(row)}
                className="p-1.5 rounded-btn hover:bg-success/10 text-success hover:text-success transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(34,197,94,0.14)] dark:hover:border-[rgba(34,197,94,0.28)]"
                title="Enable / Activate SKU"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
                Enable
              </button>
            ) : (
              <button
                onClick={() => onDelete?.(row)}
                className="p-1.5 rounded-btn hover:bg-danger/10 text-danger/80 hover:text-danger transition-colors text-xs font-semibold flex items-center gap-1 border border-border/50 hover:border-border dark:border-[rgba(255,0,0,0.14)] dark:hover:border-[rgba(255,0,0,0.28)]"
                title="Disable SKU"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                Disable
              </button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <Table
      columns={columns}
      data={items}
      loading={loading}
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
        totalItems,
        itemsPerPage,
      }}
      emptyMessage="No catalogue items match your search query."
      emptyIcon={<CatalogueIcon size={20} className="opacity-60" />}
    />
  )
}
